import { QueryClient } from '@tanstack/react-query';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { trackReactQueryError } from '@/utils/sentryHelpers';

// Type definitions for better type safety
type QueryFilters = Record<string, string | number | boolean | null | undefined>;
type ReservationStatus = 'pending' | 'confirmed' | 'cancelled';
type PartnerType = 'guide' | 'driver' | 'both';
type AvailabilityStatus = 'available' | 'busy' | 'unavailable' | null;
type CancellationFilters = {
  status?: string;
  reason?: string;
  dateFrom?: string;
  dateTo?: string;
} | null;

/**
 * 표준화된 staleTime 설정
 * 데이터 타입별로 적절한 캐싱 전략을 적용
 */
export const STALE_TIMES = {
  /** 정적 데이터 - 거의 변경되지 않는 데이터 (코스 정보, 상품 정보 등) */
  STATIC: 30 * 60 * 1000,        // 30분
  
  /** 준정적 데이터 - 자주 변경되지 않는 데이터 (사용자 프로필, 설정 등) */  
  SEMI_STATIC: 10 * 60 * 1000,   // 10분
  
  /** 동적 데이터 - 자주 변경되는 데이터 (예약 정보, 여행 상태 등) */
  DYNAMIC: 2 * 60 * 1000,        // 2분 (기본값)
  
  /** 실시간 데이터 - 매우 자주 변경되는 데이터 (좌석 현황, 실시간 알림 등) */
  REAL_TIME: 30 * 1000,          // 30초
  
  /** 인증 관련 - 보안이 중요한 데이터 */
  AUTH: 5 * 60 * 1000,           // 5분
} as const;

/**
 * React Query 클라이언트 설정
 * - Supabase와의 통합을 위한 최적화된 설정
 * - ERR_CONNECTION_CLOSED 감지 시 스마트 갱신 (페이지 리로드 없음)
 * - 우선순위 기반 복구 시스템 (인증 → 핵심 데이터 → 배경 데이터)
 * - 사용자 상태 보존 및 네트워크 최적화
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 표준화된 기본 staleTime 사용 (동적 데이터)
      staleTime: STALE_TIMES.DYNAMIC, // 2분 - 표준화된 동적 데이터 캐싱
      
      // Keep cache time reasonable
      gcTime: 10 * 60 * 1000, // 10 minutes
      
      // Smart retry logic with connection recovery (no page reload)
      retry: (failureCount, error) => {
        // Never retry AbortErrors - they're expected behavior during tab switches
        if (error instanceof Error) {
          const isAbortError = error.name === 'AbortError' || error.message?.toLowerCase().includes('abort');
          if (isAbortError) {
            console.log('⏹️ Query cancelled (AbortError) - not retrying');
            return false;
          }
          
          // Check for ERR_CONNECTION_CLOSED - just don't retry, let global handlers manage recovery
          const errorMessage = error.message || '';
          if (errorMessage.includes('ERR_CONNECTION_CLOSED')) {
            console.log('🔄 ERR_CONNECTION_CLOSED detected - skipping retry (handled by global recovery)');
            return false; // Don't retry this specific query, let global handlers manage it
          }
        }

        // Smart retry for other network errors (max 2 retries)
        if (failureCount < 2) {
          const errorMessage = error?.message?.toLowerCase() || '';
          const shouldRetry = 
            errorMessage.includes('network') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('fetch') ||
            errorMessage.includes('connection');
          
          return shouldRetry;
        }
        return false;
      },
      
      // 글로벌 에러 핸들러
      onError: (error, query) => {
        // 개발 환경이 아닌 경우에만 Sentry에 보고
        if (!import.meta.env.DEV) {
          trackReactQueryError(
            query.queryKey as string[],
            error as Error,
            query.state.data
          );
        }
        console.error('React Query Error:', error, 'Query Key:', query.queryKey);
      },
      
      // Exponential backoff with faster initial retry
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 10000),
      
      // Enhanced refetch behavior for multi-tab scenarios
      refetchOnWindowFocus: true,  // Always refetch on focus
      refetchOnReconnect: true,    // Always refetch on reconnect
      refetchOnMount: true,        // Always refetch on mount
      
      // Background refetch configuration
      refetchIntervalInBackground: false, // Don't poll in background by default
      
      // Network error handling
      networkMode: 'online', // Only run queries when online
    },
    mutations: {
      // Smart mutation retry with connection recovery (no page reload)
      retry: (failureCount, error) => {
        // Never retry AbortErrors for mutations either
        if (error instanceof Error) {
          const isAbortError = error.name === 'AbortError' || error.message?.toLowerCase().includes('abort');
          if (isAbortError) {
            console.log('⏹️ Mutation cancelled (AbortError) - not retrying');
            return false;
          }
          
          // Check for ERR_CONNECTION_CLOSED - trigger query invalidation
          const errorMessage = error.message || '';
          if (errorMessage.includes('ERR_CONNECTION_CLOSED')) {
            console.log('🔄 ERR_CONNECTION_CLOSED in mutation - skipping retry (handled by global recovery)');
            return false; // Don't retry this mutation, let global handlers manage it
          }
        }

        if (failureCount < 1) {
          const errorMessage = error?.message?.toLowerCase() || '';
          const shouldRetry = 
            errorMessage.includes('network') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('connection');
          
          return shouldRetry;
        }
        return false;
      },
      
      // 글로벌 mutation 에러 핸들러
      onError: (error, variables, context, mutation) => {
        // 개발 환경이 아닌 경우에만 Sentry에 보고
        if (!import.meta.env.DEV) {
          trackReactQueryError(
            ['mutation', mutation.options.mutationKey?.toString() || 'unknown'],
            error as Error,
            variables
          );
        }
        console.error('React Query Mutation Error:', error, 'Variables:', variables);
      },
      
      // Faster retry for mutations
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 5000),
      
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

/**
 * Smart Recovery System - 우선순위 기반 연결 복구 시스템
 * 페이지 리로드 없이 사용자 상태를 보존하면서 스마트 갱신
 */

type RecoveryReason = 'connection_closed' | 'mutation_connection_closed' | 'visibility_change' | 'network_reconnect' | 'stuck_queries';

interface RecoveryOptions {
  reason: RecoveryReason;
  priority?: 'high' | 'medium' | 'low';
  preserveUserState?: boolean;
  networkOptimized?: boolean;
}

/**
 * 우선순위 기반 스마트 복구 시스템
 * 1단계: 인증 및 세션 데이터 (최고 우선순위)
 * 2단계: 핵심 비즈니스 데이터 (예약, 결제 등)
 * 3단계: 배경 데이터 (통계, 캠시 등)
 */
export const triggerSmartRecovery = async (
  client: QueryClient, 
  reason: RecoveryReason,
  options: Partial<RecoveryOptions> = {}
) => {
  const recoveryId = `recovery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  console.log(`🚀 Starting smart recovery (${recoveryId}): ${reason}`);
  
  const startTime = performance.now();
  const config = {
    reason,
    priority: options.priority || 'high',
    preserveUserState: options.preserveUserState !== false,
    networkOptimized: options.networkOptimized !== false,
    ...options
  };

  try {
    // 페이즈 1: 인증 및 세션 복구 (최고 우선순위)
    console.log(`🔑 Phase 1: Authentication & Session Recovery`);
    await client.refetchQueries({
      queryKey: queryKeys.auth.user(),
      exact: false,
      type: 'active'
    });
    
    await client.refetchQueries({
      queryKey: queryKeys.auth.session(),
      exact: false,
      type: 'active'
    });

    // 연결 상태 확인
    await client.invalidateQueries({
      queryKey: ['connection-status'],
      exact: true
    });

    // 페이즈 2: 핵심 비즈니스 데이터 복구
    console.log(`💼 Phase 2: Core Business Data Recovery`);
    
    // 현재 활성 상태인 핵심 쿼리만 복구
    const coreQueries = [
      'reservations',
      'payments', 
      'admin-trips',
      'bookings'
    ];
    
    for (const queryType of coreQueries) {
      const activeQueries = client.getQueryCache().getAll({
        queryKey: [queryType],
        type: 'active'
      });
      
      if (activeQueries.length > 0) {
        console.log(`🔄 Recovering ${queryType} queries (${activeQueries.length} active)`);
        await client.refetchQueries({
          queryKey: [queryType],
          type: 'active',
          stale: true
        });
      }
    }

    // 페이즈 3: 배경 데이터 복구 (네트워크 최적화)
    if (config.networkOptimized && reason !== 'stuck_queries') {
      console.log(`🌐 Phase 3: Background Data Recovery (Network Optimized)`);
      
      // 비활성 쿼리는 강제로 갱신하지 않고 사용자 요청 시에만 갱신
      const backgroundQueries = [
        'products',
        'courses', 
        'users',
        'admin-trips-stats'
      ];
      
      // 비동기로 배경 데이터 복구 (사용자 체감 성능 향상)
      setTimeout(async () => {
        for (const queryType of backgroundQueries) {
          const staleQueries = client.getQueryCache().getAll({
            queryKey: [queryType],
            stale: true
          });
          
          if (staleQueries.length > 0) {
            console.log(`🌐 Background recovery: ${queryType} (${staleQueries.length} stale)`);
            client.refetchQueries({
              queryKey: [queryType],
              stale: true
            });
          }
        }
      }, 2000); // 2초 지연으로 네트워크 부하 및 사용자 체감 반응성 최적화
    }

    const endTime = performance.now();
    const recoveryTime = Math.round(endTime - startTime);
    console.log(`✅ Smart recovery completed (${recoveryId}): ${recoveryTime}ms`);
    
    // 성공 메트릭 기록
    recordRecoveryMetrics(reason, 'success', recoveryTime, client);
    
  } catch (error) {
    const endTime = performance.now();
    const recoveryTime = Math.round(endTime - startTime);
    console.error(`❌ Smart recovery failed (${recoveryId}): ${recoveryTime}ms`, error);
    
    // 실패 메트릭 기록
    recordRecoveryMetrics(reason, 'failed', recoveryTime, client, error);
    
    // Fallback: 전체 쿼리 무효화 (최후의 수단) - setTimeout으로 순환 참조 방지
    console.log('🔄 Fallback: Invalidating all active queries');
    setTimeout(() => {
      client.invalidateQueries({ type: 'active' });
    }, 100);
  }
};

/**
 * 복구 메트릭 기록 및 모니터링
 */
const recordRecoveryMetrics = (
  reason: RecoveryReason,
  result: 'success' | 'failed',
  duration: number,
  client: QueryClient,
  error?: unknown
) => {
  const metrics = {
    timestamp: new Date().toISOString(),
    reason,
    result,
    duration,
    activeQueries: client.getQueryCache().getAll({ type: 'active' }).length,
    staleQueries: client.getQueryCache().getAll({ stale: true }).length,
    errorMessage: error instanceof Error ? error.message : undefined
  };
  
  console.log(`📊 Recovery Metrics:`, metrics);
  
  // 운영 환경에서는 실제 모니터링 시스템에 전송 가능
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'query_recovery', {
      event_category: 'performance',
      event_label: reason,
      value: duration,
      custom_parameter_1: result
    });
  }
};

/**
 * 쿼리 키 팩토리
 * 일관된 쿼리 키 생성을 위한 유틸리티
 */
export const queryKeys = {
  // 인증 관련
  auth: {
    user: () => ['auth', 'user'] as const,
    session: () => ['auth', 'session'] as const,
  },
  
  // 상품 관련
  products: {
    all: () => ['products'] as const,
    list: (filters?: QueryFilters) => ['products', 'list', filters] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    search: (query: string) => ['products', 'search', query] as const,
  },
  
  // 예약 관련 (기존)
  reservations: {
    all: () => ['reservations'] as const,
    list: (userId?: string) => ['reservations', 'list', userId] as const,
    detail: (id: string) => ['reservations', 'detail', id] as const,
    byStatus: (status: ReservationStatus) => ['reservations', 'status', status] as const,
  },
  
  // 예약 관련 (새로운 - bookings 테이블)
  bookings: {
    all: () => ['bookings'] as const,
    list: (filters?: QueryFilters) => ['bookings', 'list', filters] as const,
    paginated: (filters?: QueryFilters) => ['bookings', 'paginated', filters] as const,
    detail: (id: string) => ['bookings', 'detail', id] as const,
    byUser: (userId: string) => ['bookings', 'by-user', userId] as const,
    seats: (tripId?: number) => ['bookings', 'seats', tripId] as const,
    existing: (productId?: number, userId?: string) => ['bookings', 'existing', productId, userId] as const,
    analytics: (filters?: QueryFilters) => ['bookings', 'analytics', filters] as const,
  },
  
  // 여행 관련
  trips: {
    all: () => ['trips'] as const,
    list: (filters?: QueryFilters) => ['trips', 'list', filters] as const,
    detail: (id: string | number) => ['trips', 'detail', id] as const,
    available: (productId?: number) => ['trips', 'available', productId] as const,
    byDate: (date: string) => ['trips', 'by-date', date] as const,
    upcoming: () => ['trips', 'upcoming'] as const,
    bookings: (tripId: number) => ['trips', 'bookings', tripId] as const,
  },
  
  // 코스 관련
  courses: {
    all: () => ['courses'] as const,
    list: (region?: string) => ['courses', 'list', region] as const,
    detail: (id: string) => ['courses', 'detail', id] as const,
  },
  
  // 사용자 관련
  users: {
    all: () => ['users'] as const,
    profile: (id: string) => ['users', 'profile', id] as const,
  },
  
  // 결제 관련
  payments: {
    all: () => ['payments'] as const,
    list: (userId?: string) => ['payments', 'list', userId] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
  },
  
  // 관리자 관련
  admin: {
    trips: (includePast?: boolean) => ['admin-trips', includePast] as const,
    tripsByDate: (date: string) => ['admin-trips-by-date', date] as const,
    tripsStats: () => ['admin-trips-stats'] as const,
    cancellations: (filters?: CancellationFilters) => ['admin-cancellations', filters] as const,
    partnerSchedules: (startDate: string, endDate: string, partnerType?: PartnerType, availabilityStatus?: AvailabilityStatus) => 
      ['partner-schedules', startDate, endDate, partnerType, availabilityStatus] as const,
  },
} as const;

/**
 * 에러 처리 유틸리티
 */
export const handleQueryError = (error: unknown): string => {
  console.error('Query error:', error);
  
  if (error && typeof error === 'object') {
    // Supabase 에러 처리
    if ('message' in error) {
      const message = error.message as string;
      
      // 일반적인 에러 메시지 번역
      if (message.includes('JWT expired')) {
        return '로그인이 만료되었습니다. 다시 로그인해주세요.';
      }
      
      if (message.includes('Row Level Security')) {
        return '접근 권한이 없습니다.';
      }
      
      if (message.includes('duplicate key')) {
        return '이미 존재하는 데이터입니다.';
      }
      
      if (message.includes('violates foreign key')) {
        return '참조된 데이터가 존재하지 않습니다.';
      }
      
      return message;
    }
    
    // 네트워크 에러 처리
    if ('code' in error) {
      const code = error.code as string;
      
      if (code === 'NETWORK_ERROR' || code === 'ECONNREFUSED') {
        return '네트워크 연결을 확인해주세요.';
      }
      
      if (code === 'TIMEOUT') {
        return '요청 시간이 초과되었습니다. 다시 시도해주세요.';
      }
    }
  }
  
  return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
};

/**
 * Simple debounce utility
 */
function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 * Simplified Cross-Tab Cache Sharing and Page Visibility Handling
 * 간소화된 탭 간 캐시 공유와 페이지 표시 처리
 */
if (typeof window !== 'undefined') {
  try {
    // Initialize cross-tab query synchronization with error handling
    try {
      broadcastQueryClient({
        queryClient,
        broadcastChannel: 'wholsee-query-sync', // 고유한 채널 이름
      });
      console.log('🔄 Cross-tab query synchronization enabled');
    } catch (broadcastError) {
      console.warn('⚠️ Failed to initialize cross-tab sync (using fallback):', broadcastError);
      // Continue without cross-tab sync if it fails
    }
    
    // Listen for custom auth refresh events from other tabs with debouncing
    let authRefreshTimeout: NodeJS.Timeout | null = null;
    const handleAuthRefresh = (event: Event) => {
      console.log('📡 Received auth refresh signal from another tab');
      // Debounce to prevent excessive calls
      if (authRefreshTimeout) {
        clearTimeout(authRefreshTimeout);
      }
      authRefreshTimeout = setTimeout(() => {
        queryClient.invalidateQueries({ 
          queryKey: ['connection-status'],
          exact: true 
        });
        authRefreshTimeout = null;
      }, 100);
    };
    
    window.addEventListener('supabase-auth-refresh', handleAuthRefresh);
    
    // Simple minimize/restore detection with debounced invalidation
    let lastHiddenTime = 0;
    let visibilityTimeout: NodeJS.Timeout | null = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page was minimized - record the time
        lastHiddenTime = Date.now();
        console.log('📱 Page minimized');
        // Clear any pending visibility timeout
        if (visibilityTimeout) {
          clearTimeout(visibilityTimeout);
          visibilityTimeout = null;
        }
      } else if (lastHiddenTime > 0) {
        // Page was restored - check how long it was minimized
        const hiddenDuration = Date.now() - lastHiddenTime;
        console.log(`🔄 Page restored after ${Math.round(hiddenDuration/1000)} seconds`);
        
        // Only invalidate if page was hidden for more than 5 seconds
        if (hiddenDuration > 5000) {
          console.log('🔄 Page was hidden for significant time - invalidating queries...');
          // Debounce to prevent rapid invalidations
          if (visibilityTimeout) {
            clearTimeout(visibilityTimeout);
          }
          visibilityTimeout = setTimeout(() => {
            queryClient.invalidateQueries({ type: 'active' });
            visibilityTimeout = null;
          }, 200);
        }
        
        lastHiddenTime = 0;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Network connectivity monitoring with debounced recovery
    let networkTimeout: NodeJS.Timeout | null = null;
    const handleOnline = () => {
      console.log('🌐 Network connection restored - invalidating queries...');
      // Debounce network recovery to prevent excessive calls
      if (networkTimeout) {
        clearTimeout(networkTimeout);
      }
      networkTimeout = setTimeout(() => {
        queryClient.invalidateQueries({ type: 'active' });
        networkTimeout = null;
      }, 300);
    };
    
    const handleOffline = () => {
      console.log('📡 Network connection lost - queries paused');
      console.log('⏳ Waiting for connection to resume for smart recovery...');
      // React Query automatically pauses queries when offline
      // Smart recovery will be triggered when connection is restored
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Global error handling with debounced recovery (no page reload)
    let globalErrorTimeout: NodeJS.Timeout | null = null;
    const handleGlobalError = (event: ErrorEvent) => {
      const errorMessage = event.message || '';
      if (errorMessage.includes('ERR_CONNECTION_CLOSED') || 
          errorMessage.includes('CONNECTION_CLOSED') ||
          errorMessage.includes('net::ERR_CONNECTION_CLOSED')) {
        console.log('🚨 Global ERR_CONNECTION_CLOSED detected - invalidating queries...');
        // Debounce global error handling to prevent excessive invalidations
        if (globalErrorTimeout) {
          clearTimeout(globalErrorTimeout);
        }
        globalErrorTimeout = setTimeout(() => {
          queryClient.invalidateQueries({ type: 'active' });
          globalErrorTimeout = null;
        }, 500);
      }
    };
    
    let rejectionTimeout: NodeJS.Timeout | null = null;
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = error.message as string;
        if (errorMessage.includes('ERR_CONNECTION_CLOSED') || 
            errorMessage.includes('CONNECTION_CLOSED') ||
            errorMessage.includes('net::ERR_CONNECTION_CLOSED')) {
          console.log('🚨 Unhandled rejection ERR_CONNECTION_CLOSED - invalidating queries...');
          // Debounce promise rejection handling
          if (rejectionTimeout) {
            clearTimeout(rejectionTimeout);
          }
          rejectionTimeout = setTimeout(() => {
            queryClient.invalidateQueries({ type: 'active' });
            rejectionTimeout = null;
          }, 600);
        }
      }
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      // Clear all timeouts
      if (authRefreshTimeout) clearTimeout(authRefreshTimeout);
      if (visibilityTimeout) clearTimeout(visibilityTimeout);
      if (networkTimeout) clearTimeout(networkTimeout);
      if (globalErrorTimeout) clearTimeout(globalErrorTimeout);
      if (rejectionTimeout) clearTimeout(rejectionTimeout);
      
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('supabase-auth-refresh', handleAuthRefresh);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    });
    
  } catch (error) {
    console.warn('⚠️ Failed to initialize cross-tab sync:', error);
  }
}

/**
 * 개발 환경에서만 React Query DevTools 활성화
 */
export const shouldShowDevtools = process.env.NODE_ENV === 'development';